import { Schema } from "effect"
import { Rpc, RpcGroup } from "effect/rpc"
import { Epoch, FrameSeq, Millis, NonNegInt, ProfileId, RunnerId } from "../ids"
import { StopReason } from "../run"
import { UIMessageChunk } from "../stream"

// #region run-view
/** One stored content frame of a run (mind → body), as the Run DO keeps it. */
export const RunFrame = Schema.Struct({ frameSeq: FrameSeq, at: Millis, chunk: UIMessageChunk })
export type RunFrame = typeof RunFrame.Type

export const RunViewStatus = Schema.Struct({
  phase: Schema.Literals(["waiting", "preparing", "running", "awaiting_permission", "finishing", "ended"]),
  epoch: Epoch,
  runnerId: Schema.NullOr(RunnerId),
  stopReason: Schema.NullOr(StopReason),
  // fix wave §4.5 (optional for old clients)
  /** Stored content frames of the current epoch. */
  frames: Schema.optionalKey(NonNegInt),
  profileId: Schema.optionalKey(Schema.NullOr(ProfileId)),
  /** turn.failed's error, else the last data-cli-error. */
  error: Schema.optionalKey(Schema.NullOr(Schema.Struct({ message: Schema.String }))),
  startedAt: Schema.optionalKey(Schema.NullOr(Millis)),
})
export type RunViewStatus = typeof RunViewStatus.Type
export const RunViewPhase = RunViewStatus.fields.phase

/**
 * Watch order (fix wave §4.5): `Status` first, then the backlog as several `Frames` messages (each ≤ 512 KiB
 * serialized; adjacent same-id text/reasoning deltas merged, a merged frame carries the LAST frameSeq of its range),
 * then `BacklogDone`, then live frames and a `Status` on every phase or epoch change.
 */
export const RunViewFrame = Schema.TaggedUnion({
  /** Whole frames, in frameSeq order. */
  Frames: { frames: Schema.Array(RunFrame) },
  Status: { status: RunViewStatus },
  /** The snapshot is complete; live frames follow. */
  BacklogDone: { lastFrameSeq: FrameSeq },
})
export type RunViewFrame = typeof RunViewFrame.Type

/**
 * The live transcript viewer on the Run DO (`GET /api/workspaces/:ws/runs/:runId/live`). One socket per open
 * transcript. Auth = session + workspace membership, checked by the Worker and the Run DO.
 */
export class RunRpcs extends RpcGroup.make(
  Rpc.make("Watch", { payload: { fromFrameSeq: FrameSeq }, success: RunViewFrame, stream: true }),
) {}
// #endregion run-view
